import {
	Dialog,
	DialogContent,
	DialogTitle,
	LinearProgress,
	TextField,
} from "@mui/material";
import { makeStyles } from "tss-react/mui";
import { generateErrorState, handleValidateObj } from "helpers/utils";
import React, { useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { showError } from "../../../../redux/common/actions";
import AddDialogStyle from "styles/application/AddDialogStyle";
import * as yup from "yup";

const schema = yup.object({
	quantity: yup
		.number()
		.typeError("Must be greater than 0")
		.min(1, "Must be greater than 0")
		.required("This field is required"),
	name: yup
		.string()
		.required("This field is required"),
	description: yup.string().nullable(),
});

const ADD = AddDialogStyle();
const defaultData = { quantity: 1, name: "", description: "" };
const defaultError = { quantity: null, name: null, description: null };

const useStyles = makeStyles()((theme) => ({
	dialogContent: {
		display: "flex",
		flexDirection: "column",
		minHeight: "300px",
		padding: "20px 24px",
	},
	createButton: {
		width: "auto",
	},
}));

const AddNoteDialog = ({ open, handleClose, createHandler, captions }) => {
	const { classes } = useStyles();
	const [input, setInput] = useState(defaultData);
	const [errors, setErrors] = useState(defaultError);
	const [isUpdating, setIsUpdating] = useState(false);
	const dispatch = useDispatch();
	const quantityInputRef = useRef(null);

	const closeOverride = () => {
		setInput(defaultData);
		setErrors(defaultError);

		handleClose();
	};

	const handleCreateProcess = async () => {
		setIsUpdating(true);

		try {
			const localChecker = await handleValidateObj(schema, input);
			if (!localChecker.some((el) => el.valid === false)) {
				await createHandler(input);

				closeOverride();
			} else {
				const newErrors = generateErrorState(localChecker);
				setErrors({ ...errors, ...newErrors });
			}

			setIsUpdating(false);
		} catch (err) {
			setIsUpdating(false);
			dispatch(showError("Failed to add new " + captions?.part));
			closeOverride();
		}
	};

	const handleSaveAndAddAnother = async () => {
		setIsUpdating(true);

		try {
			const localChecker = await handleValidateObj(schema, input);
			if (!localChecker.some((el) => el.valid === false)) {
				await createHandler(input);

				setInput(defaultData);
				setErrors(defaultError);
				quantityInputRef.current?.focus();
			} else {
				const newErrors = generateErrorState(localChecker);
				setErrors({ ...errors, ...newErrors });
			}

			setIsUpdating(false);
		} catch (err) {
			setIsUpdating(false);
			dispatch(showError("Failed to add new " + captions?.part));
		}
	};

	const handleEnterPress = (e) => {
		// 13 is the enter keycode
		if (!isUpdating) {
			if (e.keyCode === 13) {
				handleCreateProcess();
			}
		}
	};

	return (
		<Dialog
			open={open}
			onClose={closeOverride}
			aria-labelledby="alert-dialog-title"
			aria-describedby="alert-dialog-description"
			maxWidth="sm"
			fullWidth
		>
			{isUpdating ? <LinearProgress /> : null}
			<ADD.ActionContainer>
				<DialogTitle id="alert-dialog-title">
					{<ADD.HeaderText>Add {captions?.part}</ADD.HeaderText>}
				</DialogTitle>
				<ADD.ButtonContainer>
					<ADD.CancelButton
						onClick={closeOverride}
						variant="contained"
						style={{ width: "auto" }}
					>
						Cancel
					</ADD.CancelButton>
					<ADD.ConfirmButton
						onClick={handleSaveAndAddAnother}
						variant="contained"
						className={classes.createButton}
						disabled={isUpdating}
					>
						Save &amp; add another
					</ADD.ConfirmButton>
					<ADD.ConfirmButton
						onClick={handleCreateProcess}
						variant="contained"
						className={classes.createButton}
						disabled={isUpdating}
					>
						Save &amp; close
					</ADD.ConfirmButton>
				</ADD.ButtonContainer>
			</ADD.ActionContainer>
			<DialogContent className={classes.dialogContent}>
				<div style={{ marginBottom: "10px" }}>
					<TextField
						sx={{
							"& .MuiInputBase-input.Mui-disabled": {
								WebkitTextFillColor: "#000000",
							},
						}}
						label={captions?.partQuantity ?? "Quantity"}
						error={errors.quantity === null ? false : true}
						helperText={errors.quantity === null ? null : errors.quantity}
						fullWidth
						value={input.quantity}
						onChange={(e) => setInput({ ...input, quantity: e.target.value })}
						onKeyDown={handleEnterPress}
						type="number"
						autoFocus
						inputRef={quantityInputRef}
					/>
				</div>
				<div style={{ marginBottom: "10px" }}>
					<TextField
						sx={{
							"& .MuiInputBase-input.Mui-disabled": {
								WebkitTextFillColor: "#000000",
							},
						}}
						label={captions?.partName ?? "Part Name"}
						error={errors.name === null ? false : true}
						helperText={errors.name === null ? null : errors.name}
						fullWidth
						value={input.name}
						onChange={(e) => setInput({ ...input, name: e.target.value })}
						onKeyDown={handleEnterPress}
					/>
				</div>

				<div style={{ marginBottom: "10px" }}>
					<TextField
						sx={{
							"& .MuiInputBase-input.Mui-disabled": {
								WebkitTextFillColor: "#000000",
							},
						}}
						label={captions?.partDescription ?? "Part Description"}
						fullWidth
						multiline
						rows={4}
						value={input.description ?? ""}
						onChange={(e) =>
							setInput({ ...input, description: e.target.value })
						}
						onKeyDown={handleEnterPress}
					/>
				</div>
			</DialogContent>
		</Dialog>
	);
};

export default AddNoteDialog;
